import { IPromise, IScope } from "angular";
import { StateService } from "@uirouter/angularjs";
import { IStateInit } from "@gtmhub/core/routing";
import { IIndicator } from "@gtmhub/error-handling";
import { IGoal } from "@gtmhub/goals/models";
import { IHubSessionIdWorkflowMap } from "@gtmhub/hub/models/hub";
import { GoalsChangedEventArgs, OkrEvents } from "@gtmhub/okrs/events";
import { PlanningSessionService } from "@gtmhub/sessions/services/planning-session.service";
import { getCurrentUserId } from "@gtmhub/users";

interface IWorkflowScope extends IScope {
  indicators: {
    loading?: IIndicator;
    updating?: IIndicator;
  };
  sessionIdWorkflowMap: IHubSessionIdWorkflowMap;
  currentUserId: string;
  editReviewers(perspective: string, goalId: string): void;
  generateFakeReviewers(goal: IGoal): string[];
}

export class WorkflowCtrl implements IStateInit {
  public static $inject = ["$scope", "$state", "PlanningSessionService"];

  private okrEvents = new OkrEvents(this.$scope);

  constructor(private $scope: IWorkflowScope, private $state: StateService, private planningSessionService: PlanningSessionService) {}

  public stateInit(): IPromise<unknown> {
    return this.init();
  }

  private strings = {
    loading: "loading",
    updating: "updating",
    owner: "owner",
  };

  private init = (): IPromise<unknown> => {
    this.$scope.indicators = {
      loading: { progress: true },
    };

    this.$scope.editReviewers = this.editReviewers;
    this.$scope.generateFakeReviewers = this.generateFakeReviewers;

    this.$scope.$on("goalsChanged", this.onGoalsChanged);
    this.okrEvents.onGoalsChanged((args) => this.onGoalsUpdated(args));

    this.$scope.currentUserId = getCurrentUserId();

    return this.planningSessionService.getCurrentUserWorkflowItems().then(
      (sessionIdWorkflowMap) => {
        delete this.$scope.indicators.loading;
        this.$scope.sessionIdWorkflowMap = this.processSessionIdWorkflowMap(sessionIdWorkflowMap.hubWorkflowItems);
      },
      (err) => (this.$scope.indicators.loading = { error: err })
    );
  };

  private onGoalsUpdated = (args: GoalsChangedEventArgs): void => {
    if (args.reason === "goalChanged") {
      this.onGoalsChanged();
    }
  };

  private onGoalsChanged = (): void => {
    this.$scope.indicators.updating = { progress: true };

    this.planningSessionService.getCurrentUserWorkflowItems().then(
      (map) => {
        delete this.$scope.indicators.updating;
        this.$scope.sessionIdWorkflowMap = this.processSessionIdWorkflowMap(map.hubWorkflowItems);
      },
      (err) => (this.$scope.indicators.updating = { error: err })
    );
  };

  private editReviewers = (perspective: string, goalId: string): void => {
    if (perspective !== this.strings.owner) {
      return;
    }

    this.$state.go("._goalSidebar.goal._approve.sendForApproval", { id: goalId });
  };

  private generateFakeReviewers = (goal: IGoal): string[] => {
    const fakeArray: string[] = [];
    const reviewersNeeded: number = goal.workflow.reviewersNeeded;
    const reviewersAdded: number = goal.workflow.reviewers.length;
    const fakeArrayLength: number = reviewersNeeded - reviewersAdded > 0 ? reviewersNeeded - reviewersAdded : 0;

    for (let i = 0; i < fakeArrayLength; i++) {
      fakeArray.push("");
    }

    return fakeArray;
  };

  private processSessionIdWorkflowMap = (workflowItems: IHubSessionIdWorkflowMap): IHubSessionIdWorkflowMap => {
    return Object.values(workflowItems)
      .sort((a, b) => b.session.start.localeCompare(a.session.start))
      .reduce((workflowMap, workflowItem) => ((workflowMap[workflowItem.session.id] = workflowItem), workflowMap), {} as IHubSessionIdWorkflowMap);
  };
}
